import { Seconds } from '@/common/utils';
import { customAxiosInstance } from '@/common/api/utils/mutator';
import type {
  DtoSessionValidateSuccess,
  DtoUpdateSession,
  DtoValidateSession,
} from '@/api-client/gateway/model';
import axios from 'axios';
import dayjs from 'dayjs';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import router from '@/common/router';
import { useMessageBox } from '@/common/inject/useMessageBox';
import { i18n } from '@/common/locale';

const postApiV7SessionValidate = (params: DtoValidateSession) =>
  customAxiosInstance<DtoSessionValidateSuccess>({
    url: '/api/v7/session/validate',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
  });

const postApiV7SessionUpdate = (params: DtoUpdateSession) =>
  customAxiosInstance<string>({
    url: '/api/v7/session/update',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
  });

const useSessionTimerStore = defineStore('sessionTimer', () => {
  const { confirmMsg } = useMessageBox();
  const isOpenModal = ref<boolean>(false);

  const isTimerRunning = ref<boolean>(false);
  const remainingSessionTime = ref<Seconds>(-1);
  const displaySessionTime = computed<string>(() => {
    if (remainingSessionTime.value < 0) {
      return '00:00';
    }
    const duration = dayjs.duration(remainingSessionTime.value, 'seconds');
    const totalMinutes = Math.floor(duration.asMinutes());
    const seconds = duration.seconds();
    return `${String(totalMinutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  });
  const isExpiredSessionTime = computed<boolean>(() => remainingSessionTime.value <= 0);

  const fetchSessionTime = async (sessionToken: string) => {
    try {
      if (sessionToken) {
        const { data } = await postApiV7SessionValidate({ sessionToken });
        const { remain } = data;
        remainingSessionTime.value = remain ?? -1;
      }
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.data?.errorCode === 'GE003') {
        if (!isOpenModal.value) {
          confirmMsg({
            msgStr: i18n.global.t('MESSAGE.RESTRICT_DUPLICATE_USERS'),
            showCancelBtn: false,
            useHTML: true,
            okCallback: async () => {
              await router.push('/logout');
              isOpenModal.value = false;
            },
          });

          isOpenModal.value = true;
          remainingSessionTime.value = -1;
        }
      } else console.log(e);
    }
  };

  const updateSessionTime = async (params: DtoUpdateSession) => {
    try {
      if (Object.values(params).every((val) => !!val)) {
        await postApiV7SessionUpdate(params);
      }
    } catch (e: any) {
      console.log(e);
    }
  };

  return {
    isTimerRunning,
    isExpiredSessionTime,
    remainingSessionTime,
    displaySessionTime,
    fetchSessionTime,
    updateSessionTime,
    isOpenModal,
  };
});

export { useSessionTimerStore };
